import { Button, Stack } from "@mui/material";
import {
    type ContractParametersPP,
    instanceOfContractParametersPP,
    PendingChangeType,
    type PensionBeneficiary,
    type PensionPendingChange,
    PensionPhaseStatus,
    ProductType,
    StarPensionClientTypeEnum,
} from "@repo/api";
import { readEnv } from "@repo/helpers";
import {
    createContributionPendingChange,
    LocalStorageKeys,
    upsertContributionPendingChange,
    useAllowedChanges,
    useAppDispatch,
} from "@repo/logic";
import { BodyTextM, ChangePensionContribution, cs, HeadingL, useIsDesktop, useTypedTranslation } from "@repo/ui";
import { type FC, useCallback, useEffect, useMemo, useRef } from "react";
import { useContractId } from "../../context/ContractIDContext";
import { useContractTypeContext } from "../../context/ContractTypeContext";
import { usePrimaryAPIClient } from "../../context/PrimaryAPIClient";
import { dateTimeFormat } from "../../functions/dates";
import { useContractById, usePensionDetail } from "../../queryHooks";
import { formatAmount } from "../../utils";
import Attention from "../AttentionBlock";
import ChangeBeneficiariesButton from "../ChangeBeneficiaries";
import BeneficiariesInformationList from "../ChangeBeneficiaries/BeneficiariesInformationList";
import { ActionTypeEnum, useSmartAction } from "../Contract/SmartAction/context";
import { useDrawer } from "../Drawer/context";
import { NotInMVPButton } from "../Drawer/NotInMVP";
import { BgImage } from "../Image/BgImage";
import HelpBox from "../InfoBox/HelpBox";
import InformationList from "../InformationList";
import { TriggerButton } from "../Link";
import { mapFromV1Beneficiary } from "../ZivotniPojisteni/types";
import {
    contributionCoreSystemChangeDescription,
    employerContributionCoreSystemChangeDescription,
} from "./ContractDetail.helpers";
import ContributionDialog, { ContributionAction, type DialogValues } from "./Contribution/ContributionDialog";
import { TaxOptimumDialog } from "./TaxOptimum/TaxOptimumDialog";
// zoomImage
import zoomImage from "@repo/ui/assets/images/zoom.png";
import { AMLRedirect } from "../AML";
import { getBankIdAuthorizationUri } from "../AML/identification";

const frequencies = cs.frequencies;

// UF and PF contract detail
const ContractDetail: FC<{
    pendingChanges: PensionPendingChange[] | undefined;
    isSuccessPendingChanges: boolean;
}> = ({ pendingChanges, isSuccessPendingChanges }) => {
    const dispatch = useAppDispatch();
    const { action } = useSmartAction();
    const { showDrawer, setDrawerContent } = useDrawer();
    const { contractId: id } = useContractId();
    const { contractType } = useContractTypeContext();
    const { contractsPpApi, contractsDpsApi } = usePrimaryAPIClient();
    const { t } = useTypedTranslation();

    const {
        data: allowedChangesData,
        isSuccess: allowedChangesSuccess,
    } = useAllowedChanges({
        id,
        options: {
            enabled: Boolean(id),
        },
    });
    const { data, isSuccess, isLoading, refetch } = usePensionDetail({ id });

    const { data: contractData } = useContractById({
        id,
        options: { refetchOnMount: false },
    });

    const isDesktop = useIsDesktop();
    const useNewContributionChangeStepper =
        readEnv("GATSBY_FEATURE_IS_NEW_DPS_CONTRIBUTION_CHANGE_ENABLED") === "true" &&
        contractType === ProductType.Uf;

    const effectivePendingChanges = allowedChangesData?.pendingChanges ?? (isSuccessPendingChanges ? pendingChanges : undefined);
    const hasEffectivePendingChanges = Boolean(effectivePendingChanges && effectivePendingChanges.length > 0);
    const contributionPendingChange = effectivePendingChanges?.find(
        (change) => change.changeType === PendingChangeType.ChangeContribution,
    );

    const canChangeContributions =
        allowedChangesSuccess &&
        allowedChangesData.canChangeEmployerOrClientContribution &&
        allowedChangesData.canChangeIsEmployerContributing;

    const allowedChangesLoaded = allowedChangesSuccess && allowedChangesData;
    const {
        contract: {
            doesEmployerContribute,
            monthlyContributionClient,
            contractNumber,
            signatureDate,
            effectiveDate,
            isTaxOptimum,
            contributionFrequency,
        },
        beneficiaries,
    } = data ?? {
        clientInfo: { identificationStatus: undefined },
        contract: {
            doesEmployerContribute: undefined,
            monthlyContributionClient: undefined,
            contractNumber: undefined,
            signatureDate: undefined,
            effectiveDate: undefined,
            isTaxOptimum: undefined,
            contributionFrequency: undefined,
        },
        beneficiaries: [
            {
                firstName: undefined,
                lastName: undefined,
                dateOfBirth: undefined,
                relationship: undefined,
                ratio: undefined,
                isBank: false,
                address: {
                    street: undefined,
                    town: undefined,
                    zip: undefined,
                    country: undefined,
                },
            } as PensionBeneficiary,
        ],
    };

    const ppContract = useMemo<ContractParametersPP | undefined>(() => {
        if (data?.contract && instanceOfContractParametersPP(data.contract)) {
            return data.contract;
        }
        return undefined;
    }, [data?.contract]);

    const handleBeforeBankIdAuthorization = useCallback(() => {
        localStorage.setItem(
            LocalStorageKeys.BANK_ID_LAST_ACTION,
            JSON.stringify({
                redirect: AMLRedirect.ContributionWithQuestionare,
                contractId: id,
                contractType,
            }),
        );
    }, [id, contractType]);

    const handleChangeTaxOptimumButton: () => void = () => {
        if (!(contractType === ProductType.Uf || contractType === ProductType.Pf)) return;
        showDrawer();
        setDrawerContent(<TaxOptimumDialog contractId={id} contractType={contractType} />);
    };

    const handleChangeContributionButton = useCallback(
        (contributionAction: ContributionAction) => {
            if (!isSuccess) return;
            if (!(contractType === ProductType.Uf || contractType === ProductType.Pf)) return;
            const presetMonthlyContribution = (() => {
                if (action?.type === "Fond" && action.context && typeof action.context.newContribution === "number") {
                    return action.context.newContribution;
                }
                if (contributionPendingChange?.detail.newClientContribution) {
                    return contributionPendingChange.detail.newClientContribution;
                }
                return monthlyContributionClient;
            })();

            const currentEmployerContribution =
                contributionPendingChange?.detail.willEmployerContribute ?? data.contract.doesEmployerContribute;

            showDrawer();
            setDrawerContent(
                <ContributionDialog
                    currentValues={{
                        monthlyContribution: monthlyContributionClient ?? 0,
                        doesEmployerContribute: currentEmployerContribution ?? false,
                    }}
                    presetValues={{
                        monthlyContribution: presetMonthlyContribution ?? 0,
                        doesEmployerContribute: currentEmployerContribution ?? false,
                    }}
                    contractType={contractType}
                    contractsPpApi={contractsPpApi}
                    contractsDpsApi={contractsDpsApi}
                    onSuccess={async (values: DialogValues) => {
                        dispatch(
                            upsertContributionPendingChange({
                                contractId: id,
                                pendingChange: createContributionPendingChange(values),
                            }),
                        );
                    }}
                    onError={async () => {}}
                    id={id}
                    includeAML={
                        readEnv("GATSBY_FEATURE_IS_AML_AND_BANK_ID_ENABLED") === "true" &&
                        allowedChangesLoaded &&
                        allowedChangesData.isAmlFulfilled === false &&
                        (!("isChildrenContract" in data) || !data.isChildrenContract)
                    }
                    includeID={
                        readEnv("GATSBY_FEATURE_IS_AML_AND_BANK_ID_ENABLED") === "true" &&
                        allowedChangesLoaded &&
                        allowedChangesData.isBankIdIdentificationRequired === true &&
                        data.clientType === StarPensionClientTypeEnum.Z &&
                        (!("isChildrenContract" in data) || !data.isChildrenContract)
                    }
                    contributionAction={contributionAction}
                />,
            );
        },
        [
            action?.context,
            action?.type,
            contractType,
            contractsDpsApi,
            contractsPpApi,
            data,
            id,
            isSuccess,
            monthlyContributionClient,
            contributionPendingChange,
            effectivePendingChanges,
            dispatch,
            setDrawerContent,
            showDrawer,
        ],
    );

    const changeContributionButton = (
        <>
            {(contractType === ProductType.Uf || contractType === ProductType.Pf) && (
                <>
                    {useNewContributionChangeStepper ? (
                        <ChangePensionContribution
                            kind="detail"
                            disabled={!canChangeContributions}
                            bankIdAuthorizationUri={getBankIdAuthorizationUri()}
                            contractId={id}
                            contractType={contractType}
                            beforeBankIdAuthorizationAction={handleBeforeBankIdAuthorization}
                            buttonText={t("investmentStrategy.changePensionContribution.changeTextShort")}
                        />
                    ) : (
                        <Button
                            variant="text"
                            onClick={() => {
                                handleChangeContributionButton(ContributionAction.Monthly);
                            }}
                            key="employer-contribution"
                            data-test="pension-monthly-contribution-button"
                            disabled={!canChangeContributions}
                        >
                            <BodyTextM>Změnit částku</BodyTextM>
                        </Button>
                    )}
                </>
            )}
        </>
    );

    const changeEmployerContributesButton = (
        <>
            {(contractType === ProductType.Uf || contractType === ProductType.Pf) && (
                <>
                    {useNewContributionChangeStepper ? (
                        <ChangePensionContribution
                            kind="detail"
                            disabled={!canChangeContributions}
                            bankIdAuthorizationUri={getBankIdAuthorizationUri()}
                            contractId={id}
                            contractType={contractType}
                            beforeBankIdAuthorizationAction={handleBeforeBankIdAuthorization}
                            scrollToEmployerSection
                            buttonText={t("investmentStrategy.changePensionContribution.changeTextShort")}
                        />
                    ) : (
                        <Button
                            variant="text"
                            onClick={() => {
                                handleChangeContributionButton(ContributionAction.Employer);
                            }}
                            key="employer-contribution"
                            data-test="pension-employer-contribution-button"
                            disabled={!canChangeContributions}
                        >
                            <BodyTextM>Změnit</BodyTextM>
                        </Button>
                    )}
                </>
            )}
        </>
    );

    const handleChangeContributionButtonRef = useRef(handleChangeContributionButton);
    handleChangeContributionButtonRef.current = handleChangeContributionButton;

    useEffect(() => {
        if (isSuccess) {
            // show dialog after data is loaded
            if (action?.type === ActionTypeEnum.Fond || action?.type === ActionTypeEnum.FondBankId) {
                handleChangeContributionButtonRef.current(ContributionAction.Monthly);
            }
            if (action?.type === ActionTypeEnum.EmployerContribution) {
                handleChangeContributionButtonRef.current(ContributionAction.Employer);
            }
        }
    }, [action?.type, isSuccess]);

    return (
        <>
            <BgImage
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- we know it's an image
                image={isDesktop ? zoomImage : undefined}
                style={{
                    backgroundPosition: "right center",
                    backgroundSize: "250px 261px",
                }}
            >
                <Stack
                    sx={{
                        width: { md: "68.8%" },
                    }}
                >
                    <HeadingL withoutScale>Detail smlouvy</HeadingL>
                    {contractData?.pensionPhaseStatus === PensionPhaseStatus.Payout && (
                        <Attention severity="info">{cs.global.cantChangeContractDataTerminatedContract}</Attention>
                    )}
                    <InformationList
                        isLoading={isLoading}
                        information={[
                            {
                                title: "Číslo smlouvy",
                                description: contractNumber ?? "",
                            },
                            {
                                title: "Datum podpisu",
                                description: signatureDate ? dateTimeFormat.format(new Date(signatureDate)) : "",
                            },
                            {
                                title: "Datum účinnosti",
                                description: effectiveDate ? dateTimeFormat.format(new Date(effectiveDate)) : "-",
                            },
                            contractType === ProductType.Pf && {
                                title: "Výsluhová penze sjednána",
                                description: ppContract?.isEarlyRetirement ? "Ano" : "Ne",
                            },
                            contractType === ProductType.Pf &&
                                ppContract?.isEarlyRetirement === true && {
                                    title: "Nárok na výsluhovou penzi",
                                    description: ppContract.earlyRetirementDate
                                        ? dateTimeFormat.format(new Date(ppContract.earlyRetirementDate))
                                        : "Bez nároku",
                                },
                            contractType === ProductType.Pf && {
                                title: "Penzijní plán",
                                description: ppContract?.pensionPlan ? `Číslo ${String(ppContract.pensionPlan)}` : "-",
                            },
                            contractData?.pensionPhaseStatus === PensionPhaseStatus.Saving &&
                                !contractData.isChildrenContract && {
                                    title: cs.taxOptimumDialog.wantTaxOptimum,
                                    description: isTaxOptimum ? "Ano" : "Ne",
                                    additionalElements: [
                                        <TriggerButton
                                            disabled={!allowedChangesLoaded || !allowedChangesData.canChangeTaxOptimum}
                                            variant="text"
                                            key="tax-optimum-button"
                                            size="small"
                                            onClick={handleChangeTaxOptimumButton}
                                        >
                                            {cs.global.change}
                                        </TriggerButton>,
                                    ],
                                    secondRow: (
                                        <>
                                            {allowedChangesLoaded && !allowedChangesData.canChangeTaxOptimum && (
                                                <BodyTextM color="info.main">
                                                    {allowedChangesData.taxOptimumDescription}
                                                </BodyTextM>
                                            )}
                                        </>
                                    ),
                                },
                            contractData?.pensionPhaseStatus === PensionPhaseStatus.Saving && {
                                title: "Můj měsíční příspěvek",
                                description: monthlyContributionClient
                                    ? `${formatAmount(monthlyContributionClient)} Kč`
                                    : "",
                                additionalElements: [changeContributionButton],
                                valueSelector: "pension-monthly-contribution-value",
                                secondRow: (
                                    <>
                                        {
                                            // primary present failed pending changes, rather than core system changes
                                            allowedChangesLoaded &&
                                            !allowedChangesData.canChangeEmployerOrClientContribution && allowedChangesData.contributionDescription ? (
                                                <BodyTextM color="info.main">
                                                    {allowedChangesData.contributionDescription}
                                                </BodyTextM>
                                            ) : (
                                                hasEffectivePendingChanges &&
                                                contributionCoreSystemChangeDescription(
                                                    effectivePendingChanges,
                                                    monthlyContributionClient,
                                                )
                                                    ?.filter(Boolean)
                                                    .map((s) => (
                                                        <BodyTextM key={s} color="info.main">
                                                            {s}
                                                        </BodyTextM>
                                                    ))
                                            )
                                        }
                                    </>
                                ),
                            },
                            contractData?.pensionPhaseStatus === PensionPhaseStatus.Saving &&
                                !contractData.isChildrenContract && {
                                    title: "Příspěvek zaměstnavatele",
                                    description: doesEmployerContribute
                                        ? "Zaměstnavatel mi přispívá"
                                        : "Zaměstnavatel mi nepřispívá",
                                    additionalElements: [changeEmployerContributesButton],
                                    valueSelector: "pension-employer-contribution-value",
                                    secondRow: (
                                        <>
                                            {
                                                // primary present failed pending changes, rather than core system changes
                                                allowedChangesLoaded &&
                                                !allowedChangesData.canChangeIsEmployerContributing ? (
                                                    <BodyTextM color="info.main">
                                                        {allowedChangesData.isEmployerContributingDescription}
                                                    </BodyTextM>
                                                ) : (
                                                    hasEffectivePendingChanges && (
                                                        <BodyTextM color="info.main">
                                                            {employerContributionCoreSystemChangeDescription(
                                                                effectivePendingChanges,
                                                                Boolean(doesEmployerContribute),
                                                            )}
                                                        </BodyTextM>
                                                    )
                                                )
                                            }
                                        </>
                                    ),
                                },
                            contractData?.pensionPhaseStatus === PensionPhaseStatus.Saving &&
                                contractType === ProductType.Pf && {
                                    title: "Sjednaná výše měsíčního příspěvku třetí osoby",
                                    description: `${ppContract?.monthlyContributionOthers ? formatAmount(ppContract.monthlyContributionOthers) : "-"} Kč`,
                                },
                            contractData?.pensionPhaseStatus === PensionPhaseStatus.Saving && {
                                title: "Frekvence placení",
                                description: contributionFrequency ? frequencies[contributionFrequency] : "",
                            },
                        ]}
                    />
                    {hasEffectivePendingChanges && (
                        <Attention severity="info" sx={{ mt: 4 }}>
                            {cs.contributionAttention}
                        </Attention>
                    )}
                </Stack>
            </BgImage>
            {contractData?.pensionPhaseStatus === PensionPhaseStatus.Saving && (
                <Stack>
                    <Stack
                        sx={{
                            flexDirection: { md: "row" },
                            alignItems: { md: "baseline" },
                        }}
                    >
                        <HeadingL withoutScale data-test="dps-beneficiaries-home-page-header">
                            {cs.product.beneficiaries[contractType]}
                        </HeadingL>
                    </Stack>
                    <Stack spacing={{ xs: 4, md: 0 }}>
                        <BeneficiariesInformationList
                            beneficiaries={beneficiaries.map(mapFromV1Beneficiary)}
                            isLoading={isLoading}
                        />
                        {(contractType === ProductType.Uf &&
                            readEnv("GATSBY_FEATURE_IS_DPS_BENEFICIENTS_CHANGE_ENABLED") === "true") ||
                        (contractType === ProductType.Pf &&
                            readEnv("GATSBY_FEATURE_IS_PP_BENEFICIENTS_CHANGE_ENABLED") === "true") ? (
                            <ChangeBeneficiariesButton
                                beneficiaries={beneficiaries}
                                onSuccess={async () => {
                                    await refetch();
                                }}
                                onError={async () => {
                                    await refetch();
                                }}
                                isLoading={isLoading || !allowedChangesSuccess}
                                canChangeBeneficiaries={allowedChangesData?.canChangeBeneficiaries}
                                disabledReasons={allowedChangesData?.reasons}
                                customDisabledReason={
                                    allowedChangesSuccess &&
                                    allowedChangesData.canChangeBeneficiaries !== true &&
                                    allowedChangesData.beneficiariesDescription
                                        ? allowedChangesData.beneficiariesDescription
                                        : undefined
                                }
                            />
                        ) : (
                            <NotInMVPButton
                                text={cs.product.beneficiaries.changeBeneficiar.default}
                                onClick={undefined}
                            />
                        )}
                    </Stack>
                </Stack>
            )}

            <HelpBox noImage={false} />
        </>
    );
};
export default ContractDetail;
